import React, { FC, ReactElement, useCallback, useMemo, useState } from "react"
import { Form, Formik } from "formik"

import { CMSService } from "../../../services/cms/cms.service"
import { Tenant, TenantColorsData } from "../../../types/api"
import { formTenantColorsData } from "../../../util/editorsForms"
import { ToolTip } from "../components/ToolTip"

import { FormControl } from "./FormControl"
import { GradientBackgroundFields } from "./GradientBackgroundFields"
import { ModalsColorFields } from "./ModalsColorFields"
import { OriginalsColorFields } from "./OriginalsColorFields"
import { TextGradientFields } from "./TextGradientFields"
import { TitleDividersGradientFields } from "./TitleDividersGradientFields"
import { WalletButtonGradientFields } from "./WalletButtonGradientFields"

const colorsFields: { name: string; title: string; tooltip?: string }[] = [
  {
    name: "primary",
    title: "Primary color",
    tooltip: "Primary color is used for actions buttons borders in the header"
  },
  { name: "secondary", title: "Secondary color", tooltip: "This color is applied to the main page background" },
  { name: "header", title: "Header color", tooltip: "Header color is used for top navigation bar background" },
  { name: "success", title: "Success color", tooltip: "Success color is used for colors withe " },
  { name: "viewMoreColor", title: "View More button color", tooltip: "'VIEW MORE' button color" },
  {
    name: "accordionBorderColor",
    title: "Accordion border color",
    tooltip: "Accordion border color, for vertical left border as in the 'FREQUENTLY ASKED QUESTIONS' accordion"
  },
  {
    name: "iconLinesColor",
    title: "Icon lines color",
    tooltip: "For the icons lines colors, as for the top left menu icon"
  },
  {
    name: "internalNavDotDividerColor",
    title: "Internal navigation dot divider color",
    tooltip: "There are horizontal navigations on some pages, divided by dots. This is the dot color"
  },
  {
    name: "internalNavWrappingBorderColor",
    title: "Internal navigation wrapping border color",
    tooltip: "There are horizontal navigations on some pages and some of them have wrapping borders - this is the color for them"
  },
  {
    name: "cardBorder",
    title: "Card border color",
    tooltip: "Card color is used on the cards borders on the wallet deposit and wallet withdraw page"
  },
  {
    name: "contentButtonTextColor",
    title: "Content button text color",
    tooltip: "The text color of the button with some background color"
  },
  {
    name: "outlinedButtonTextColor",
    title: "Outlined button text color",
    tooltip: "The text color of the button without background color and with wrapping border"
  }
]

type Props = {
  tenant: Tenant
}

/**
 * Applied on submit only, so Formik may hold partial edits while the group is
 * open. Stored shape: absent block = derive; a stored block carries only real
 * colours — the editor reads any object as "overrides enabled", so `{}` and
 * blank values must never be persisted. A gradient is kept only with >= 2
 * stops (linear-gradient() is invalid with one).
 */
const withCleanOriginals = (values: TenantColorsData): TenantColorsData => {
  const originals = values.originals
  if (!originals) return { ...values, originals: undefined }

  const entries = Object.entries(originals)
    .map(([key, value]) => [key, Array.isArray(value) ? value.filter(Boolean) : value] as const)
    .filter(([, value]) => (Array.isArray(value) ? value.length >= 2 : Boolean(value)))

  return { ...values, originals: entries.length ? (Object.fromEntries(entries) as TenantColorsData["originals"]) : undefined }
}

export const EditorForm: FC<Props> = ({ tenant }): ReactElement => {
  const [loading, setLoading] = useState<"loading" | false>(false)
  const onSubmit = useCallback(async (values: TenantColorsData) => {
    setLoading("loading")
    // Colors only. Sending the cloned tenant would write back a setup_data snapshot taken at
    // mount, silently reverting anything saved elsewhere in the meantime.
    await CMSService.updateTenantData(tenant.id, { color_theme: withCleanOriginals(values) })
    setLoading(false)
  }, [])

  const initialColorTheme = useMemo(() => formTenantColorsData(tenant), [tenant])

  return (
    <Formik initialValues={initialColorTheme} onSubmit={onSubmit}>
      {({ handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          <h3>Colors values</h3>
          {colorsFields.map(({ name, title, tooltip }, i) => (
            <FormControl key={i} name={name} title={title} tooltip={tooltip} />
          ))}
          <ModalsColorFields />
          <GradientBackgroundFields />
          <TextGradientFields />
          <TitleDividersGradientFields />
          <div className="relative group">
            <ToolTip text="These are colors for wallet icon button in the header. Gradient colors, hover color and box shadow color" />
            <WalletButtonGradientFields />
            <FormControl name="walletHoverBg" title="Wallet button hover background" />
            <FormControl name="walletBoxShadow" title="Wallet button box shadow color" />
          </div>
          <OriginalsColorFields />
          <button className={`btn btn-primary mt-8 ${loading || ""}`} type="submit" disabled={!!loading}>
            Save
          </button>
        </Form>
      )}
    </Formik>
  )
}
